import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, ArrowLeft, Eye, EyeOff, Loader2, ShieldAlert } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@services/api';
import { useAuthStore } from '@store/authStore';

const resetSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetFormData = z.infer<typeof resetSchema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const isForced = searchParams.get('forced') === 'true';

  const { isAuthenticated, user, updateUser } = useAuthStore();
  const isForcedReset = isForced || Boolean(isAuthenticated && user?.mustChangePassword);

  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  const { mutate: executeReset, isPending } = useMutation({
    mutationFn: async (password: string) => {
      if (isForcedReset && isAuthenticated) {
        return authApi.changePassword({ newPassword: password });
      }
      if (token) {
        return authApi.resetPassword({ token, password });
      }
      throw new Error('Invalid request context');
    },
    onSuccess: () => {
      if (isForcedReset && isAuthenticated) {
        updateUser({ mustChangePassword: false });
      }
      setIsSuccess(true);
      setApiError('');
    },
    onError: (err: any) => {
      setApiError(
        err.response?.data?.message || err.message || 'Failed to reset password. Please try again.'
      );
    },
  });

  const onSubmit = (data: ResetFormData) => {
    executeReset(data.password);
  };

  if (isSuccess) {
    return (
      <div className="text-center animate-fade-in">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Password reset successful</h2>
        <p className="text-muted-foreground text-sm mb-6">
          Your password has been successfully updated.
        </p>
        {isAuthenticated ? (
          <button
            onClick={() => navigate('/dashboard', { replace: true })}
            className="w-full inline-block bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Continue to Dashboard
          </button>
        ) : (
          <Link
            to="/login"
            className="w-full inline-block bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Continue to login
          </Link>
        )}
      </div>
    );
  }

  if (!token && !isForcedReset) {
    return (
      <div className="text-center animate-fade-in">
        <h2 className="text-xl font-bold text-foreground mb-2">Invalid link</h2>
        <p className="text-muted-foreground text-sm mb-6">
          This password reset link is invalid or has expired.
        </p>
        <Link
          to="/forgot-password"
          className="text-primary hover:underline flex items-center justify-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Request new link
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h2 className="text-2xl font-bold text-foreground mb-1">
        {isForcedReset ? 'Reset Your Default Password' : 'Set new password'}
      </h2>
      <p className="text-muted-foreground text-sm mb-6">
        {isForcedReset
          ? 'You logged in with a default password. Please choose a secure custom password to proceed.'
          : 'Please enter your new password below.'}
      </p>

      {isForcedReset && (
        <div className="mb-5 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3.5 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2.5">
          <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <span>
            First time login detected. For account security, you must set a new password before continuing to the platform.
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1.5">New Password</label>
          <div className="relative">
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-destructive text-xs mt-1">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">Confirm Password</label>
          <div className="relative">
            <input
              {...register('confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-destructive text-xs mt-1">{errors.confirmPassword.message}</p>
          )}
        </div>

        {apiError && (
          <div className="bg-destructive/10 text-destructive text-sm px-3 py-2 rounded-lg">
            {apiError}
          </div>
        )}

        <button
          type="submit"
          disabled={isPending}
          className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          {isPending ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}
